import type { StatusTone } from "@playbookai/ui";
import type { SolveStatus } from "@playbookai/types";

/**
 * DS design-token class map for the 5 Solve statuses (PB-162 A8/C7). Verified
 * maps to the `pb-green` (healthy) token — the same token the DS StatusPill
 * `healthy` tone reads — the green success reading the prototype's `emerald-500`
 * intended (restored in PB-254; was pb-teal under the PB-156 baseline).
 */
export const STATUS_TONE: Record<SolveStatus, string> = {
  Draft: "bg-pb-card-elevated text-pb-text-muted border-pb-border",
  Active: "bg-pb-cyan-soft text-pb-cyan-text border-pb-cyan/40",
  Verified: "bg-pb-green-soft text-pb-green-text border-pb-green/40",
  Closed: "bg-pb-card-elevated text-pb-text-secondary border-pb-border",
  Archived: "bg-pb-card-elevated text-pb-text-muted border-pb-border",
};

/**
 * SolveStatus → DS StatusPill semantic tone (PB-162 C7). Used where the status
 * renders as a `StatusPill` (Workspace Settings). Verified → healthy
 * resolves to the same `pb-green` token as `STATUS_TONE.Verified` above, so the
 * dashboard Badge and the workspace pill stay colour-consistent.
 */
export const STATUS_PILL_TONE: Record<SolveStatus, StatusTone> = {
  Draft: "neutral",
  Active: "info",
  Verified: "healthy",
  Closed: "neutral",
  Archived: "neutral",
};

export function formatRelative(ts: string): string {
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString();
}

export function formatDate(ts: string): string {
  const time = new Date(ts).getTime();
  return Number.isFinite(time) ? new Date(time).toLocaleDateString() : "—";
}
